import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Container, Form, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { createATACProject } from 'data-access/atac.api';

const DOMAIN_OPTIONS = [
  { value: '', label: 'Select Domain' },
  { value: 'Enterprise AI', label: 'Enterprise AI' },
  { value: 'Banking', label: 'Banking' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Life Sciences', label: 'Life Sciences' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Pharma', label: 'Pharma' },
  { value: 'CPG', label: 'CPG' },
  { value: 'Other', label: 'Other' }
];

const ATEPlusCreateProject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [projectTitle, setProjectTitle] = useState('');
  const [description, setDescription] = useState('');
  const [domainName, setDomainName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!projectTitle.trim()) {
      setError('Project title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createATACProject({
        projectTitle: projectTitle.trim(),
        description: description.trim() || undefined,
        domainName: domainName.trim() || undefined
      });

      const project = response.data?.project ?? response.data;
      const projectId = (project as { projectId?: string })?.projectId;

      if (projectId) {
        localStorage.setItem('projectId', projectId);
      }

      dispatch(
        setToast({
          show: true,
          title: 'Project created',
          message: 'Your ATE+ project has been created successfully.'
        })
      );

      if (projectId) {
        navigate(`/ate+/project/${encodeURIComponent(projectId)}`);
      } else {
        navigate('/ate+/all-projects');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create project';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <h2 className="mb-4">Create New Project</h2>
      <p className="text-body-secondary mb-4">Create a new ATE+ AI Trust Evaluation project</p>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={8} lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Project Title *</Form.Label>
              <Form.Control
                type="text"
                value={projectTitle}
                onChange={e => setProjectTitle(e.target.value)}
                placeholder="Enter project title"
                required
                disabled={isSubmitting}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter description (optional)"
                disabled={isSubmitting}
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Domain</Form.Label>
              <Form.Select value={domainName} onChange={e => setDomainName(e.target.value)} disabled={isSubmitting}>
                {DOMAIN_OPTIONS.map(opt => (
                  <option key={opt.value || 'empty'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                {error}
              </div>
            )}
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default ATEPlusCreateProject;
